import { activateInput, api, type ActivateInput, queryKeys } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { formatLds, formatUsd, TOKEN_LABEL } from '../../lib/format';
import { Alert, Button, Card, FormGroup, Input, Modal, Spinner } from '../../ui';

export const PACKAGE_TABS = [
  { label: 'Activate', to: '/activation' },
  { label: 'History', to: '/activation/history' },
];

export function Activate() {
  const queryClient = useQueryClient();
  const [successOpen, setSuccessOpen] = useState(false);

  const { data: packages, isLoading } = useQuery({
    queryKey: queryKeys.packages(),
    queryFn: () => api.packages.list().then((r) => r.data),
  });

  const form = useForm<ActivateInput>({
    resolver: zodResolver(activateInput),
    defaultValues: { package_id: '', target_user_code: '', transaction_pin: '' },
  });

  const selectedId = form.watch('package_id');
  const targetUserCode = form.watch('target_user_code');

  // There is currently only ever one active package, so there is nothing to
  // "choose" — auto-select it instead of requiring an extra click on a card
  // that visually looks like a static info panel. If more packages are ever
  // added, the picker below reappears automatically.
  useEffect(() => {
    if (packages?.length === 1 && !selectedId) {
      form.setValue('package_id', packages[0].id);
    }
  }, [packages, selectedId, form]);

  // Debounce the target user id so the preview doesn't refetch on every keystroke.
  const [debouncedTarget, setDebouncedTarget] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTarget(targetUserCode ?? ''), 400);
    return () => clearTimeout(timer);
  }, [targetUserCode]);

  // The LDS quantity for a $100 package floats with the token rate, so the
  // quote is fetched fresh rather than computed in the browser.
  const preview = useQuery({
    queryKey: ['activation-preview', selectedId, debouncedTarget],
    queryFn: () => api.activations.preview(selectedId, debouncedTarget || undefined).then((r) => r.data),
    enabled: !!selectedId,
  });

  const activate = useMutation({
    mutationFn: (values: ActivateInput) => api.activations.activate(values),
    onSuccess: () => {
      form.reset();
      setSuccessOpen(true);
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet() });
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader
        title="Activate Package"
        description={`One-time $100 activation in ${TOKEN_LABEL} Token unlocks the Alpha One ecosystem.`}
        tabs={PACKAGE_TABS}
      />

      {packages && packages.length > 1 ? (
        <div className="pkg-grid">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`pkg-card ${selectedId === pkg.id ? 'featured' : ''}`}
              onClick={() => form.setValue('package_id', pkg.id)}
            >
              <h3>{pkg.name}</h3>
              <p className="pkg-price">{formatUsd(pkg.amount_usd)}</p>
              <p className="text-muted small">{pkg.description}</p>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        {activate.error ? <Alert tone="danger">{(activate.error as Error).message}</Alert> : null}
        {preview.data && !preview.data.can_activate ? (
          <Alert tone="warning">{preview.data.reason ?? 'This package cannot be activated right now.'}</Alert>
        ) : null}

        <form onSubmit={form.handleSubmit((values) => activate.mutate(values))} noValidate>
          {preview.data ? (
            <dl className="quote-list">
              <div>
                <dt>Required</dt>
                <dd>{formatLds(preview.data.required_lds)}</dd>
              </div>
              <div>
                <dt>LDS rate</dt>
                <dd>{formatUsd(preview.data.lds_rate_usd)}</dd>
              </div>
              <div>
                <dt>Your activation wallet</dt>
                <dd>{formatUsd(preview.data.activation_balance)}</dd>
              </div>
            </dl>
          ) : null}

          <FormGroup
            label="Activate for a downline member (optional)"
            hint="Leave blank to activate your own account"
            error={form.formState.errors.target_user_code?.message}
          >
            <Input placeholder="AO123456" {...form.register('target_user_code')} />
          </FormGroup>

          <FormGroup label="Transaction PIN" error={form.formState.errors.transaction_pin?.message}>
            <Input type="password" {...form.register('transaction_pin')} />
          </FormGroup>

          <Button
            type="submit"
            variant="gold"
            disabled={activate.isPending || !selectedId || preview.data?.can_activate === false}
          >
            {activate.isPending ? 'Activating…' : 'Activate Now'}
          </Button>
        </form>
      </Card>

      <Modal
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        title="Activation Successful"
        size="sm"
        footer={
          <Button variant="gold" onClick={() => setSuccessOpen(false)}>
            Done
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <CheckCircle2 size={48} color="#c9a84c" />
          <p style={{ textAlign: 'center', margin: 0 }}>
            Your package has been activated. Referral and level income have been dispatched to your upline.
          </p>
        </div>
      </Modal>
    </>
  );
}
